import {AppActions, RootState} from 'app/rootReducer';
import {store} from 'app/store';
import Toast from 'components/Basic/Toast';
import {push} from 'connected-react-router';
import {progressActions} from 'features/Member/Progress/progressSlice';
import {paymentActions} from 'features/Payment';
import {userActions} from 'features/User';
import {wellniteExerciseActions} from 'features/WellniteExercise';
import {ExerciseStatus} from 'features/WellniteExercise/utils/constants';
import {
  SliceStatus,
  StripePlanNames,
  SuperCoachAction,
  SuperCoachChatMessage,
  SuperCoachChatMessageStreamed,
  SuperCoachEvent,
  UserRoles,
} from 'interfaces';
import {Epic, ofType} from 'redux-observable';
import {
  catchError,
  concat,
  concatMap,
  filter,
  from,
  ignoreElements,
  map,
  mapTo,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import {SuperCoachService} from 'services/api';
import {isSubscribedToAddonPlan} from 'utils';
import {v4 as uuid} from 'uuid';

import {superCoachActions} from './superCoachSlice';

const POLL_INTERVAL = 1 * 60 * 1000;

/**
 * Polling Epic:
 * - When the user logs-in or restores a session, checks if it's a patient and subscribed to SuperCoach
 * - Starts a timer(0, POLL_INTERVAL) to dispatch getSuperCoachRecommendations()
 * - Stops if the user logs out
 */
export const recommendationsPollingEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) => {
  const startPolling$ = merge(
    action$.pipe(ofType(userActions.setUser.type)),
    action$.pipe(ofType(userActions.checkSessionSuccess.type)),
    action$.pipe(ofType(paymentActions.createAddonSubscriptionSuccess.type)),
  ).pipe(
    withLatestFrom(state$),
    filter(([, state]) => {
      return (
        state.user.current?.role === UserRoles.member &&
        isSubscribedToAddonPlan(state.user.current, 'Super Coach')
      );
    }),
    // eslint-disable-next-line no-console
    tap(() => console.debug('[SuperCoach] polling starting for patient')),
  );

  return startPolling$.pipe(
    switchMap(() =>
      timer(0, POLL_INTERVAL).pipe(
        mapTo(superCoachActions.getSuperCoachRecommendations({})),
        takeUntil(action$.pipe(ofType(userActions.logout.type))),
      ),
    ),
  );
};

/**
 * Fetch recommendations.
 */
export const fetchRecommendationsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachRecommendations.match),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        state.superCoach.sendChatMessageStatus !== SliceStatus.pending ||
        state.superCoach.getMessagesStatus !== SliceStatus.pending,
    ),
    mergeMap(([{payload}]) =>
      from(SuperCoachService.getSuperCoachRecommendations(payload)).pipe(
        concatMap(resp =>
          concat(
            of(
              superCoachActions.getSuperCoachRecommendationsSuccess(
                resp.data.message,
              ),
            ),
            timer(1000).pipe(
              mapTo(
                superCoachActions.toggleSuperCoachIsStreaming(
                  resp.data.message as SuperCoachChatMessage,
                ),
              ),
            ),
          ),
        ),
        catchError((err: Error) =>
          of(
            superCoachActions.getSuperCoachRecommendationsFailure(err.message),
          ),
        ),
      ),
    ),
  );

/**
 * After getting a recommendation from SuperCoach, check if the user is on
 * Coach screen and if they aren't, show a toast message.
 */
export const showRecommendationsToastEpic: Epic<
  AppActions,
  never,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachRecommendationsSuccess.match),
    withLatestFrom(state$),
    filter(([, state]) => !state.superCoach.isOnCoachScreen),
    tap(([{payload}]) => {
      if (!payload) {
        return;
      }

      const shorten = (text: string, maxLen = 150) => {
        const shortened = text.substring(0, maxLen);
        return text.length > maxLen ? `${shortened}...` : shortened;
      };

      let title = 'SuperCoach';
      if (payload.actionRecommendations?.[0]?.title?.length) {
        title = payload.actionRecommendations?.[0]?.title;
      }

      let message = 'New SuperCoach recommendations are waiting for you!';
      if (payload.response?.length) {
        message = payload.response;
      }

      Toast({
        type: 'info',
        title: shorten(title),
        message: shorten(message),
        pauseOnHover: true,
        closeOnClick: false,
        callback: () => store.dispatch(push('/experiences/coach')),
      });
    }),
    ignoreElements(),
  );

export const sendSuperCoachOnboardingDataEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachOnboardingData.match),
    mergeMap(({payload: {onSuccess, ...payload}}) =>
      from(SuperCoachService.sendSuperCoachOnboardingData(payload)).pipe(
        concatMap(() => {
          if (onSuccess) {
            onSuccess();
          }

          return of(
            superCoachActions.sendSuperCoachOnboardingDataSuccess(true),
          );
        }),
        catchError((message: string) =>
          concat(
            of(superCoachActions.sendSuperCoachOnboardingDataFailure(message)),
          ),
        ),
      ),
    ),
  );

export const sendSuperCoachOnboardingFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachOnboardingDataFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const getSuperCoachChatMessagesEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachChatMessages.match),
    mergeMap(({payload: {onSuccess, ...payload}}) =>
      from(SuperCoachService.getSuperCoachChatMessages(payload)).pipe(
        concatMap(result => {
          const pipes = of(
            superCoachActions.getSuperCoachChatMessagesSuccess(
              result.data.message,
            ),
          );
          if (onSuccess) {
            onSuccess(result.data.message);
          }
          return pipes;
        }),

        catchError((message: string) =>
          concat(
            of(superCoachActions.getSuperCoachChatMessagesFailure(message)),
          ),
        ),
      ),
    ),
  );

export const getSuperCoachChatMessagesFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachChatMessagesFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const sendSuperCoachChatMessageEpic: Epic = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachChatMessage.match),
    mergeMap(
      ({payload}) =>
        new Observable(observer => {
          const eventSource =
            SuperCoachService.sendSuperCoachChatMessage(payload);
          let response = ``;

          eventSource.onmessage = event => {
            try {
              const parsedData: SuperCoachChatMessageStreamed = JSON.parse(
                event.data,
              );

              if (parsedData.done) {
                eventSource.close();

                // Close the connection immediately, but delay calling the action to let the animations finish
                setTimeout(() => {
                  observer.next(
                    superCoachActions.endSuperCoachChatMessageStreamChunk({
                      message: parsedData.message,
                      recommendedMessage: parsedData.recommendedMessage,
                    }),
                  );

                  const isActionOrContent =
                    !!parsedData.message?.actionRecommendations?.length ||
                    !!parsedData.message?.contentRecommendations?.length;

                  if (!isActionOrContent && !parsedData.recommendedMessage) {
                    observer.complete();
                  } else {
                    setTimeout(() => {
                      observer.next(
                        superCoachActions.toggleSuperCoachIsStreaming(
                          parsedData.message,
                        ),
                      );
                      observer.next(
                        superCoachActions.toggleSuperCoachIsStreaming(
                          parsedData.recommendedMessage,
                        ),
                      );
                      observer.complete();
                    }, 1000);
                  }
                }, response.length * 10);

                return;
              }

              if (parsedData.text) {
                response += parsedData.text;
                observer.next(
                  superCoachActions.receiveSuperCoachChatMessageStreamChunk({
                    text: parsedData.text,
                  }),
                );
              }
            } catch (error) {
              observer.next(
                superCoachActions.sendSuperCoachChatMessageFailure(
                  'Error parsing event data',
                ),
              );
            }
          };

          eventSource.onerror = () => {
            observer.next(
              superCoachActions.sendSuperCoachChatMessageFailure('API error'),
            );
            observer.complete();
            eventSource?.close();
          };

          return () => {
            eventSource?.close();
          };
        }),
    ),
  );

export const sendSuperCoachChatMessageFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachChatMessageFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const saveSuperCoachActionFeedbackEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.saveSuperCoachActionFeedback.match),
    mergeMap(({payload}) =>
      from(SuperCoachService.saveSuperCoachActionFeedback(payload)).pipe(
        concatMap(() => {
          return of(superCoachActions.saveSuperCoachActionFeedbackSuccess());
        }),
        catchError((message: string) =>
          concat(
            of(superCoachActions.saveSuperCoachActionFeedbackFailure(message)),
          ),
        ),
      ),
    ),
  );

export const saveSuperCoachActionFeedbackFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.saveSuperCoachActionFeedbackFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

/**
 * Whenever the user subscribes to SuperCoach, show a congratulation message.
 */
export const onCreateAddonSubscriptionEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(paymentActions.createAddonSubscriptionSuccess.match),
    filter(({payload}) => payload.priceId === StripePlanNames.superCoach),
    withLatestFrom(state$),
    filter(([, state]) => !!state.superCoach.isOnCoachScreen),
    mergeMap(() => {
      const payload = {
        _id: uuid(),
        question: '',
        response: '',
        actionRecommendations: [
          {
            action: SuperCoachAction.showOnSubscriptionMessage,
            title: '',
          },
        ],
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      return concat(
        of(superCoachActions.getSuperCoachRecommendationsSuccess(payload)),
        timer(1000).pipe(
          mapTo(superCoachActions.toggleSuperCoachIsStreaming(payload)),
        ),
      );
    }),
  );

/**
 * When the user completes the exercise, run this engagement.
 */
export const onExerciseCompletedEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(wellniteExerciseActions.updateExerciseAnswersSuccess.match),
    filter(({payload}) => payload.status === ExerciseStatus.completed),
    map(({payload}) =>
      superCoachActions.getSuperCoachRecommendations({
        event: SuperCoachEvent.onExerciseCompleted,
        eventPayload: payload._id,
      }),
    ),
  );

/**
 * When the user updates their PHQ9 & GAD7, run this engagement.
 */
export const onProgressUpdatedEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(progressActions.updateProgressAssessment.fulfilled.match),
    map(({payload}) =>
      superCoachActions.getSuperCoachRecommendations({
        event: SuperCoachEvent.onProgressUpdated,
        eventPayload: payload.map(item => item._id),
      }),
    ),
  );

export const superCoachEpics = [
  showRecommendationsToastEpic,
  recommendationsPollingEpic,
  fetchRecommendationsEpic,
  sendSuperCoachOnboardingDataEpic,
  sendSuperCoachOnboardingFailureEpic,
  getSuperCoachChatMessagesEpic,
  getSuperCoachChatMessagesFailureEpic,
  sendSuperCoachChatMessageEpic,
  sendSuperCoachChatMessageFailureEpic,
  saveSuperCoachActionFeedbackEpic,
  saveSuperCoachActionFeedbackFailureEpic,
  onCreateAddonSubscriptionEpic,
  onExerciseCompletedEpic,
  onProgressUpdatedEpic,
];
